/*
 * Copyright 2026 North Carolina State University
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import { ipmApi, errorMessage, fileUrl } from '../api';
import { formFields, formatValue } from '../fieldTypes';
import EntryDetail from './EntryDetail';
import EntryForm from './EntryForm';

// Types too long or rich for a table cell; they show in the entry view.
const SKIP_IN_TABLE = new Set(['textarea', 'images', 'table']);
const MAX_COLUMNS = 5;

const entryDateText = (form, entry) => {
  const id = form.entry_date_field;
  const raw = id && entry.data?.[id];
  return raw ? formatValue({ type: 'date' }, raw) : new Date(entry.entry_date || entry.createdAt).toLocaleDateString();
};

const Cell = ({ field, value }) => {
  if (field.type === 'files' && Array.isArray(value) && value.length) {
    return value.map(fl => (
      <div key={fl.id}><a href={fileUrl(fl.id)} target="_blank" rel="noreferrer">{fl.name}</a></div>
    ));
  }
  return formatValue(field, value) || '—';
};

const matches = (entry, fields, q) => fields.some(f => formatValue(f, entry.data?.[f.id]).toLowerCase().includes(q));

/**
 * A form's entries. By default a few key columns with the newest first; with
 * `allColumns`, every field as a column (the List of Controls) plus a search.
 */
const EntriesList = ({ form, refreshKey, allColumns = false, title, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(allColumns ? 50 : 10);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fields = useMemo(() => formFields(form), [form]);
  const currentIds = useMemo(() => new Set(fields.map(f => f.id)), [fields]);
  const columns = useMemo(
    () => (allColumns
      ? fields.filter(f => f.type !== 'images')
      : fields.filter(f => !SKIP_IN_TABLE.has(f.type) && f.id !== form.entry_date_field).slice(0, MAX_COLUMNS)),
    [fields, form.entry_date_field, allColumns]
  );
  const q = query.trim().toLowerCase();
  const shown = q ? rows.filter(e => matches(e, fields, q)) : rows;
  const showDate = !allColumns;
  const span = columns.length + (showDate ? 3 : 2);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ipmApi.entries({ form: form._id, limit: pageSize, skip: page * pageSize });
      setRows(res.entries);
      setTotal(res.total);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not load entries'));
    } finally {
      setLoading(false);
    }
  }, [form._id, page, pageSize]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setPage(0); }, [form._id]);

  const confirmDelete = async () => {
    try {
      await ipmApi.deleteEntry(deleting._id);
      toast.success('Entry deleted');
      setDeleting(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not delete the entry'));
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6">
          {title || `${form.name} entries`} {total > 0 && <span className="ipm-muted">({total})</span>}
        </Typography>
        {allColumns && (
          <TextField size="small" placeholder="Search this page…" value={query} onChange={e => setQuery(e.target.value)}
            inputProps={{ 'aria-label': 'Search entries' }} sx={{ minWidth: 260 }} />
        )}
      </Box>
      <TableContainer component={Paper} variant="outlined" className={allColumns ? 'ipm-wide-table' : undefined}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {showDate && <TableCell>Date</TableCell>}
              {columns.map(c => <TableCell key={c.id}>{c.label}</TableCell>)}
              <TableCell>Entered by</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={span} align="center"><CircularProgress size={22} /></TableCell></TableRow>
            )}
            {!loading && shown.length === 0 && (
              <TableRow><TableCell colSpan={span} align="center" className="ipm-muted">{q ? 'Nothing matches.' : 'No entries yet.'}</TableCell></TableRow>
            )}
            {shown.map(entry => (
              <TableRow key={entry._id} hover>
                {showDate && <TableCell sx={{ whiteSpace: 'nowrap' }}>{entryDateText(form, entry)}</TableCell>}
                {columns.map(c => <TableCell key={c.id}><Cell field={c} value={entry.data?.[c.id]} /></TableCell>)}
                <TableCell>{entry.created_by?.name || '—'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="View"><IconButton size="small" aria-label="View entry" onClick={() => setViewing(entry)}><VisibilityOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" aria-label="Edit entry" onClick={() => setEditing(entry)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" aria-label="Delete entry" onClick={() => setDeleting(entry)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={pageSize}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={allColumns ? [25, 50, 100] : [10, 25, 50]}
        />
      </TableContainer>

      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="md" fullWidth>
        <DialogTitle>{form.name} — {viewing && (allColumns ? formatValue({ type: 'text' }, viewing.data?.[form.title_field]) || 'entry' : entryDateText(form, viewing))}</DialogTitle>
        <DialogContent dividers>{viewing && <EntryDetail entry={viewing} currentFieldIds={currentIds} />}</DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditing(viewing); setViewing(null); }}>Edit</Button>
          <Button onClick={() => setViewing(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit {form.name} entry</DialogTitle>
        <DialogContent dividers>
          {editing && (
            <EntryForm form={form} entry={editing} onCancel={() => setEditing(null)}
              onSaved={() => { setEditing(null); load(); onChanged?.(); }} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete this entry?</DialogTitle>
        <DialogContent>
          {allColumns
            ? <>“{deleting && (formatValue({ type: 'text' }, deleting.data?.[form.title_field]) || 'This entry')}” and any files on it will be permanently removed.</>
            : <>The {form.name} entry from {deleting && entryDateText(form, deleting)} and any images or files on it will be permanently removed.</>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EntriesList;
